import { useEffect, useState, ChangeEvent } from 'react';
import { medicamentosService } from '../services/medicamentosService';
import { queixasService } from '../services/queixasService';
import { sintomasService } from '../services/sintomasService';
import { humorService } from '../services/humorService';
import { problemasClinicosService } from '../services/problemasClinicosService';

interface CatalogoItem {
  id: string;
  valor: string;
  descricao?: string;
  categoria?: string;
  ordem?: number;
  ativo?: boolean;
  // Campos específicos
  tipo_avaliacao?: 'intensidade' | 'frequencia' | 'ambos';
  cor_indicativa?: string;
  codigo?: string;
  cid10?: string;
  dsm5?: string;
}

interface CatalogoData {
  queixas: CatalogoItem[];
  sintomas: CatalogoItem[];
  humor: CatalogoItem[];
  problemasClinicos: CatalogoItem[];
  medicamentos: CatalogoItem[];
}

type CatalogoTipo = 'queixas' | 'sintomas' | 'humor' | 'problemasClinicos' | 'medicamentos';

export default function PersonalizarPage() {
  const [catalogos, setCatalogos] = useState<CatalogoData>({
    queixas: [],
    sintomas: [],
    humor: [],
    problemasClinicos: [],
    medicamentos: []
  });
  
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  
  // Estado para controle da interface
  const [abaSelecionada, setAbaSelecionada] = useState<CatalogoTipo>('queixas');
  const [modoEdicao, setModoEdicao] = useState(false);
  const [itemEditando, setItemEditando] = useState<CatalogoItem | null>(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [itemParaExcluir, setItemParaExcluir] = useState<CatalogoItem | null>(null);
  const [mostrarInativos, setMostrarInativos] = useState(false);

  // Form state para novo/editar item
  const [formData, setFormData] = useState<CatalogoItem>({
    id: '',
    valor: '',
    descricao: '',
    categoria: '',
    ordem: 0,
    ativo: true
  });

  // Configurações específicas por tipo de catálogo
  const configCatalogos = {
    queixas: {
      titulo: 'Queixas',
      icon: 'fas fa-comments',
      color: 'primary',
      categorias: ['Emocional', 'Comportamental', 'Cognitiva', 'Física', 'Social', 'Familiar'],
      campos: ['valor', 'descricao', 'categoria', 'ordem']
    },
    sintomas: {
      titulo: 'Sintomas',
      icon: 'fas fa-stethoscope',
      color: 'info',
      categorias: ['Físico', 'Psicológico', 'Comportamental', 'Cognitivo', 'Social'],
      campos: ['valor', 'descricao', 'categoria', 'tipo_avaliacao', 'ordem']
    },
    humor: {
      titulo: 'Estados de Humor',
      icon: 'fas fa-smile',
      color: 'warning',
      categorias: ['Eutímico', 'Depressivo', 'Maníaco', 'Ansioso', 'Irritável', 'Eufórico'],
      campos: ['valor', 'descricao', 'categoria', 'cor_indicativa', 'ordem']
    },
    problemasClinicos: {
      titulo: 'Problemas Clínicos',
      icon: 'fas fa-diagnoses',
      color: 'danger',
      categorias: ['Transtornos de Humor', 'Transtornos de Ansiedade', 'Transtornos de Personalidade', 'Transtornos Psicóticos', 'Outros'],
      campos: ['valor', 'codigo', 'descricao', 'categoria', 'cid10', 'dsm5', 'ordem']
    },
    medicamentos: {
      titulo: 'Medicamentos',
      icon: 'fas fa-pills',
      color: 'success',
      categorias: ['Antidepressivo', 'Ansiolítico', 'Antipsicótico', 'Estabilizador de Humor', 'Estimulante', 'Outros'],
      campos: ['valor', 'descricao', 'categoria', 'ordem']
    }
  };

  // Carregar dados inicialmente e quando mostrarInativos mudar
  useEffect(() => {
    carregarCatalogos();
  }, [mostrarInativos]); // Recarregar quando o toggle mudar

  const carregarCatalogos = async () => {
    try {
      setCarregando(true);
      setErro(null);
      
      // console.log('[PersonalizarPage] Carregando catálogos de todos os services dedicados...', {
        // mostrarInativos
      // });
      
      // Buscar cada catálogo do seu service dedicado em paralelo
      // IMPORTANTE: Passar o parâmetro mostrarInativos para incluir itens inativos quando o toggle estiver ativo
      const [queixas, sintomas, humor, problemasClinicos, medicamentos] = await Promise.all([
        queixasService.obterTodos(mostrarInativos),
        sintomasService.obterTodos(mostrarInativos),
        humorService.obterTodos(mostrarInativos),
        problemasClinicosService.obterTodos(mostrarInativos),
        medicamentosService.obterTodos(mostrarInativos)
      ]);
      
      // console.log('[PersonalizarPage] Catálogos carregados:', {
        // queixas: queixas.length,
        // sintomas: sintomas.length,
        // humor: humor.length,
        // problemasClinicos: problemasClinicos.length,
        // medicamentos: medicamentos.length,
        // mostrarInativos
      // });
      
      // Transformar dados para incluir campos adicionais
      const catalogosTransformados: CatalogoData = {
        queixas: Array.isArray(queixas) ? queixas.map(transformarItem) : [],
        sintomas: Array.isArray(sintomas) ? sintomas.map(transformarItem) : [],
        humor: Array.isArray(humor) ? humor.map(transformarItem) : [],
        problemasClinicos: Array.isArray(problemasClinicos) ? problemasClinicos.map(transformarItem) : [],
        medicamentos: Array.isArray(medicamentos) ? medicamentos.map(transformarItemMedicamento) : []
      };
      
      setCatalogos(catalogosTransformados);
    } catch (error) {
      console.error('[PersonalizarPage] Erro ao carregar catálogos:', error);
      setErro('Não foi possível carregar os catálogos. Verifique sua conexão e tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  const transformarItem = (item: any): CatalogoItem => {
    if (typeof item === 'string') {
      return {
        id: `temp_${Date.now()}_${Math.random()}`,
        valor: item,
        descricao: '',
        categoria: '',
        ordem: 0,
        ativo: true
      };
    }
    
    // Para problemas clínicos, garantir que id seja numérico e codigo seja preservado
    const itemId = item.id !== undefined ? String(item.id) : `temp_${Date.now()}_${Math.random()}`;
    
    // CRÍTICO: Converter corretamente o campo ativo do MySQL
    // MySQL retorna: 1 (true) ou 0 (false)
    // Precisamos normalizar para boolean verdadeiro
    const ativoBoolean = item.ativo === true || item.ativo === 1 || item.ativo === '1';
    
    return {
      id: itemId,
      valor: item.valor || item.label || '',
      descricao: item.descricao ?? '',
      categoria: item.categoria ?? '',
      ordem: item.ordem ?? 0,
      ativo: ativoBoolean,  // ✅ Usar valor corretamente convertido
      tipo_avaliacao: item.tipo_avaliacao,
      cor_indicativa: item.cor_indicativa,
      codigo: item.codigo,
      cid10: item.cid10,
      dsm5: item.dsm5
    };
  };

  const transformarItemMedicamento = (item: any): CatalogoItem => {
    // CRÍTICO: Converter corretamente o campo ativo do MySQL
    const ativoBoolean = item.ativo === true || item.ativo === 1 || item.ativo === '1';
    
    return {
      id: item.id ? String(item.id) : `temp_${Date.now()}_${Math.random()}`,
      valor: item.nome_medicamento ?? '',
      descricao: item.descricao ?? '',
      categoria: item.categoria ?? '',
      ordem: item.ordem ?? 0,
      ativo: ativoBoolean  // ✅ Usar valor corretamente convertido
    };
  };

  const iniciarEdicao = (item?: CatalogoItem) => {
    if (item) {
      setFormData({ ...item });
      setItemEditando(item);
    } else {
      const novoItem: CatalogoItem = {
        id: '',
        valor: '',
        descricao: '',
        categoria: '',
        ordem: catalogos[abaSelecionada].length,
        ativo: true
      };
      
      // Adicionar campos específicos baseados no tipo
      if (abaSelecionada === 'sintomas') {
        novoItem.tipo_avaliacao = 'ambos';
      } else if (abaSelecionada === 'humor') {
        novoItem.cor_indicativa = '#007bff';
      } else if (abaSelecionada === 'problemasClinicos') {
        novoItem.codigo = '';
        novoItem.cid10 = '';
        novoItem.dsm5 = '';
      }
      
      setFormData(novoItem);
      setItemEditando(null);
    }
    setModoEdicao(true);
  };

  const cancelarEdicao = () => {
    setModoEdicao(false);
    setItemEditando(null);
    setFormData({
      id: '',
      valor: '',
      descricao: '',
      categoria: '',
      ordem: 0,
      ativo: true
    });
  };

  const adicionarItem = async (tipo: CatalogoTipo, item: CatalogoItem) => {
    try {
      // console.log(`[PersonalizarPage] Adicionando item do tipo ${tipo}:`, item);
      
      switch (tipo) {
        case 'queixas':
          await queixasService.adicionar({
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            ordem: item.ordem ?? 0
          });
          break;
          
        case 'sintomas':
          await sintomasService.adicionar({
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            tipo_avaliacao: item.tipo_avaliacao,
            ordem: item.ordem ?? 0
          });
          break;
          
        case 'humor':
          await humorService.adicionar({
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            cor_indicativa: item.cor_indicativa ?? '',
            ordem: item.ordem ?? 0
          });
          break;
          
        case 'problemasClinicos':
          await problemasClinicosService.adicionar({
            codigo: item.codigo || item.id || item.valor.toLowerCase().replace(/\s+/g, '_'),
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            cid10: item.cid10 ?? '',
            dsm5: item.dsm5 ?? '',
            ordem: item.ordem ?? 0
          });
          break;
          
        case 'medicamentos':
          await medicamentosService.adicionar({
            nome_medicamento: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            ordem: item.ordem ?? 0
          });
          break;
          
        default:
          throw new Error(`Tipo de catálogo não suportado: ${tipo}`);
      }
      
      // console.log(`[PersonalizarPage] Item adicionado com sucesso no tipo ${tipo}`);
    } catch (error) {
      console.error(`[PersonalizarPage] Erro ao adicionar item do tipo ${tipo}:`, error);
      throw error;
    }
  };

  const salvarItem = async () => {
    if (!formData.valor.trim()) {
      setErro('O campo "Valor" é obrigatório.');
      return;
    }

    try {
      setSalvando(true);
      setErro(null);

      // SIMPLIFICAÇÃO: Remover validação redundante de sessão
      // O ProtectedRoute já garante que o usuário está autenticado
      
      // console.log('[PersonalizarPage] Salvando item:', {
        // tipo: abaSelecionada,
        // valor: formData.valor
      // });

      if (itemEditando) {
        // Atualizar item existente
        await atualizarItem(abaSelecionada, formData);
      } else {
        // Adicionar novo item
        await adicionarItem(abaSelecionada, formData);
      }

      await carregarCatalogos();
      cancelarEdicao();
      setSucesso(`Item ${itemEditando ? 'atualizado' : 'adicionado'} com sucesso!`);
      
      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => setSucesso(null), 3000);
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      
      // SIMPLIFICAÇÃO: Tratamento de erro mais direto
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        const status = axiosError.response?.status;
        
        if (status === 401) {
          setErro('Sessão expirada. A página será recarregada.');
          // Forçar reload em vez de logout manual
          setTimeout(() => window.location.reload(), 2000);
          return;
        } else if (status === 404) {
          setErro('Funcionalidade não encontrada no servidor.');
          return;
        } else if (status === 500) {
          setErro('Erro interno do servidor. Tente novamente.');
          return;
        }
      }
      
      setErro(`Não foi possível ${itemEditando ? 'atualizar' : 'adicionar'} o item.`);
    } finally {
      setSalvando(false);
    }
  };

  const atualizarItem = async (tipo: CatalogoTipo, item: CatalogoItem) => {
    try {
      // console.log(`[PersonalizarPage] Atualizando item do tipo ${tipo}:`, item);
      
      switch (tipo) {
        case 'queixas':
          await queixasService.atualizar(Number(item.id), {
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            ordem: item.ordem ?? 0,
            ativo: item.ativo !== false
          });
          break;
        
        case 'sintomas':
          await sintomasService.atualizar(Number(item.id), {
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            tipo_avaliacao: item.tipo_avaliacao,
            ordem: item.ordem ?? 0,
            ativo: item.ativo !== false
          });
          break;
        
        case 'humor':
          await humorService.atualizar(Number(item.id), {
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            cor_indicativa: item.cor_indicativa ?? '',
            ordem: item.ordem ?? 0,
            ativo: item.ativo !== false
          });
          break;
        
        case 'problemasClinicos':
          // console.log('[PersonalizarPage] Atualizando problema clínico:', { 
            // itemId: item.id, 
            // itemIdType: typeof item.id,
            // convertedId: Number(item.id),
            // isNaN: isNaN(Number(item.id))
          // });
          
          await problemasClinicosService.atualizar(Number(item.id), {
            codigo: item.codigo || item.id,
            valor: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            cid10: item.cid10 ?? '',
            dsm5: item.dsm5 ?? '',
            ordem: item.ordem ?? 0,
            ativo: item.ativo !== false
          });
          break;
        
        case 'medicamentos':
          await medicamentosService.atualizar(Number(item.id), {
            nome_medicamento: item.valor,
            descricao: item.descricao ?? '',
            categoria: item.categoria ?? '',
            ordem: item.ordem ?? 0,
            ativo: item.ativo !== false
          });
          break;
        
        default:
          // console.log(`[PersonalizarPage] Atualização não implementada para tipo ${tipo}`);
          break;
      }
      
      // console.log(`[PersonalizarPage] Item atualizado com sucesso no tipo ${tipo}`);
    } catch (error) {
      console.error(`[PersonalizarPage] Erro ao atualizar item do tipo ${tipo}:`, error);
      throw error;
    }
  };

  const confirmarExclusao = (item: CatalogoItem) => {
    setItemParaExcluir(item);
    setMostrarModal(true);
  };

  const excluirItem = async () => {
    if (!itemParaExcluir) return;

    try {
      setSalvando(true);
      let resultado: any;
      
      // Primeira tentativa: delete normal (com proteção inteligente)
      switch (abaSelecionada) {
        case 'queixas':
          resultado = await queixasService.excluir(Number(itemParaExcluir.id));
          break;
        case 'sintomas':
          resultado = await sintomasService.excluir(Number(itemParaExcluir.id));
          break;
        case 'humor':
          resultado = await humorService.excluir(Number(itemParaExcluir.id));
          break;
        case 'problemasClinicos':
          resultado = await problemasClinicosService.excluir(Number(itemParaExcluir.id));
          break;
        case 'medicamentos':
          resultado = await medicamentosService.excluir(Number(itemParaExcluir.id));
          break;
      }

      // console.log('[PersonalizarPage] Resultado da exclusão:', resultado);

      // Se foi soft delete (item em uso), perguntar se quer forçar
      if (resultado?.tipo === 'soft_delete') {
        const confirmarForcado = window.confirm(
          `⚠️ ATENÇÃO:\n\n` +
          `${resultado.mensagem}\n\n` +
          `O item foi DESATIVADO para preservar o histórico dos prontuários.\n\n` +
          `Deseja EXCLUIR PERMANENTEMENTE mesmo assim?\n` +
          `(Isso pode afetar ${resultado.quantidadeProntuarios} prontuário(s))`
        );

        if (confirmarForcado) {
          // Segundo delete: forçar exclusão permanente
          switch (abaSelecionada) {
            case 'queixas':
              resultado = await queixasService.excluir(Number(itemParaExcluir.id), true);
              break;
            case 'sintomas':
              resultado = await sintomasService.excluir(Number(itemParaExcluir.id), true);
              break;
            case 'humor':
              resultado = await humorService.excluir(Number(itemParaExcluir.id), true);
              break;
            case 'medicamentos':
              resultado = await medicamentosService.excluir(Number(itemParaExcluir.id), true);
              break;
          }
          
          setSucesso(`✅ Item excluído permanentemente!`);
        } else {
          setSucesso(`ℹ️ Item desativado (preservado no histórico)`);
        }
      } else if (resultado?.tipo === 'hard_delete') {
        setSucesso('✅ Item excluído permanentemente!');
      } else {
        setSucesso('Item excluído com sucesso!');
      }

      await carregarCatalogos();
      setMostrarModal(false);
      setItemParaExcluir(null);
      setTimeout(() => setSucesso(null), 3000);
    } catch (error) {
      console.error('[PersonalizarPage] Erro ao excluir item:', error);
      setErro('Não foi possível excluir o item.');
      setTimeout(() => setErro(null), 3000);
    } finally {
      setSalvando(false);
    }
  };

  const reativarItem = async (item: CatalogoItem) => {
    try {
      setSalvando(true);
      // console.log(`[PersonalizarPage] Reativando item do tipo ${abaSelecionada}:`, item);
      
      // Criar cópia do item com ativo = true
      const itemReativado = { ...item, ativo: true };
      
      // Usar a função de atualizar para reativar
      await atualizarItem(abaSelecionada, itemReativado);
      
      await carregarCatalogos();
      setSucesso('Item reativado com sucesso!');
      setTimeout(() => setSucesso(null), 3000);
    } catch (error) {
      console.error('[PersonalizarPage] Erro ao reativar item:', error);
      setErro('Não foi possível reativar o item.');
      setTimeout(() => setErro(null), 3000);
    } finally {
      setSalvando(false);
    }
  };

  const renderFormulario = () => {
    const config = configCatalogos[abaSelecionada];
    
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className={config.icon}></i>
            {itemEditando ? ' Editar' : ' Adicionar'} {config.titulo.slice(0, -1)}
          </h3>
          <div className="card-tools">
            <button type="button" className="btn btn-sm btn-secondary" onClick={cancelarEdicao}>
              <i className="fas fa-times"></i> Cancelar
            </button>
          </div>
        </div>
        <div className="card-body">
          <form onSubmit={(e) => { e.preventDefault(); salvarItem(); }}>
            <div className="row">
              {/* Campo Valor - sempre obrigatório */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold" htmlFor="personalizarValor">
                  Valor *
                </label>
                <input
                  id="personalizarValor"
                  type="text"
                  className="form-control"
                  value={formData.valor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, valor: e.target.value })}
                  placeholder={`Nome do ${config.titulo.toLowerCase().slice(0, -1)}`}
                  required
                />
              </div>

              {/* Campo Categoria */}
              {config.campos.includes('categoria') && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold" htmlFor="personalizarCategoria">Categoria</label>
                  <select
                    id="personalizarCategoria"
                    className="form-select"
                    value={formData.categoria ?? ''}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, categoria: e.target.value })}
                  >
                    <option value="">Selecione uma categoria</option>
                    {config.categorias.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Campo Código - apenas para problemas clínicos */}
              {config.campos.includes('codigo') && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold" htmlFor="personalizarCodigo">Código</label>
                  <input
                    id="personalizarCodigo"
                    type="text"
                    className="form-control"
                    value={formData.codigo ?? ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, codigo: e.target.value })}
                    placeholder="Ex: depressao_maior"
                  />
                </div>
              )}

              {/* Campo Descrição */}
              {config.campos.includes('descricao') && (
                <div className="col-12 mb-3">
                  <label className="form-label fw-bold" htmlFor="personalizarDescricao">Descrição</label>
                  <textarea
                    id="personalizarDescricao"
                    className="form-control"
                    rows={3}
                    value={formData.descricao ?? ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, descricao: e.target.value })}
                    placeholder="Descrição detalhada (opcional)"
                  />
                </div>
              )}

              {/* Campos específicos por tipo */}
              {abaSelecionada === 'sintomas' && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold" htmlFor="personalizarTipoAvaliacao">Tipo de Avaliação</label>
                  <select
                    id="personalizarTipoAvaliacao"
                    className="form-select"
                    value={formData.tipo_avaliacao || 'ambos'}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, tipo_avaliacao: e.target.value as any })}
                  >
                    <option value="intensidade">Intensidade</option>
                    <option value="frequencia">Frequência</option>
                    <option value="ambos">Ambos</option>
                  </select>
                </div>
              )}

              {abaSelecionada === 'humor' && (
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-bold" htmlFor="personalizarCorIndicativa">Cor Indicativa</label>
                  <div className="input-group">
                    <input
                      id="personalizarCorIndicativa"
                      type="color"
                      className="form-control form-control-color"
                      value={formData.cor_indicativa || '#007bff'}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cor_indicativa: e.target.value })}
                    />
                    <input
                      type="text"
                      className="form-control"
                      value={formData.cor_indicativa || '#007bff'}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cor_indicativa: e.target.value })}
                      placeholder="#007bff"
                    />
                  </div>
                </div>
              )}

              {abaSelecionada === 'problemasClinicos' && (
                <>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold" htmlFor="personalizarCid10">CID-10</label>
                    <input
                      id="personalizarCid10"
                      type="text"
                      className="form-control"
                      value={formData.cid10 ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cid10: e.target.value })}
                      placeholder="Ex: F32.9"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-bold" htmlFor="personalizarDsm5">DSM-5</label>
                    <input
                      id="personalizarDsm5"
                      type="text"
                      className="form-control"
                      value={formData.dsm5 ?? ''}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, dsm5: e.target.value })}
                      placeholder="Ex: 296.22"
                    />
                  </div>
                </>
              )}

              {/* Campo Ordem */}
              <div className="col-md-6 mb-3">
                <label className="form-label fw-bold" htmlFor="personalizarOrdem">Ordem de Exibição</label>
                <input
                  id="personalizarOrdem"
                  type="number"
                  className="form-control"
                  value={formData.ordem ?? 0}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, ordem: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>

              {/* Campo Ativo */}
              <div className="col-md-6 mb-3 d-flex align-items-center">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="ativo"
                    checked={formData.ativo}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, ativo: e.target.checked })}
                  />
                  <label className="form-check-label fw-bold" htmlFor="ativo">
                    Item Ativo
                  </label>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelarEdicao}
                disabled={salvando}
              >
                <i className="fas fa-times"></i> Cancelar
              </button>
              <button
                type="submit"
                className={`btn btn-${config.color}`}
                disabled={salvando || !formData.valor.trim()}
              >
                {salvando ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Salvando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    {itemEditando ? ' Atualizar' : ' Adicionar'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderLista = () => {
    const config = configCatalogos[abaSelecionada];
    const items = catalogos[abaSelecionada];
    
    // Não precisa mais filtrar aqui - o backend já está filtrando baseado em mostrarInativos

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            <i className={config.icon}></i> {config.titulo}
          </h3>
          <div className="card-tools">
            <button
              type="button"
              className={`btn btn-sm btn-${config.color}`}
              onClick={() => iniciarEdicao()}
            >
              <i className="fas fa-plus"></i> Adicionar
            </button>
          </div>
        </div>
        {/* Barra de opções secundárias */}
        <div className="card-body border-bottom pb-2 d-flex flex-wrap align-items-center gap-3">
          <div className="form-check m-0">
            <input
              type="checkbox"
              className="form-check-input"
              id="mostrarInativos"
              checked={mostrarInativos}
              onChange={(e) => setMostrarInativos(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="mostrarInativos">
              <i className="fas fa-eye-slash me-1"></i>
              Mostrar inativos
            </label>
            {mostrarInativos && items.filter(i => !i.ativo).length > 0 && (
              <small className="text-muted ms-2">({items.filter(i => !i.ativo).length} inativos)</small>
            )}
          </div>
        </div>
        <div className="card-body p-0">
          {items.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <i className={`${config.icon} fa-3x mb-3`}></i>
              <p>{mostrarInativos ? 'Nenhum item encontrado.' : 'Nenhum item ativo cadastrado.'}</p>
              <button className={`btn btn-${config.color}`} onClick={() => iniciarEdicao()}>
                <i className="fas fa-plus"></i> Adicionar Primeiro Item
              </button>
            </div>
          ) : (
            <>
              {/* Renderização MOBILE - cards empilhados */}
              <div className="d-block d-md-none catalogo-mobile-list p-3">
                {items
                  .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
                  .map((item, index) => (
                    <div key={item.id} className={`catalogo-mobile-card mb-3 ${!item.ativo ? 'inativo' : ''}`}>
                      <div className="d-flex justify-content-between align-items-start catalogo-mobile-header">
                        <div>
                          <span className="badge badge-secondary me-2">{item.ordem || index}</span>
                          <strong className={`catalogo-mobile-valor ${!item.ativo ? 'text-muted' : ''}`}>{item.valor}</strong>
                          {item.categoria && (
                            <span className={`badge badge-${config.color} ms-2`}>{item.categoria}</span>
                          )}
                        </div>
                        <span className={`badge badge-${item.ativo ? 'success' : 'secondary'}`}>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                      </div>
                      {item.descricao && <div className="text-muted small mt-1">{item.descricao}</div>}
                      {abaSelecionada === 'sintomas' && (
                        <div className="mt-1"><span className="badge badge-info">{item.tipo_avaliacao || 'ambos'}</span></div>
                      )}
                      {abaSelecionada === 'humor' && item.cor_indicativa && (
                        <div className="d-flex align-items-center mt-1">
                          <div className="rounded-circle me-2" style={{ width: 18, height: 18, backgroundColor: item.cor_indicativa }}></div>
                          <small>{item.cor_indicativa}</small>
                        </div>
                      )}
                      {abaSelecionada === 'problemasClinicos' && (
                        <div className="mt-1 small">
                          {item.codigo && <div><strong>Código:</strong> <code>{item.codigo}</code></div>}
                          {(item.cid10 || item.dsm5) && (
                            <div>
                              {item.cid10 && <span className="me-2">CID: {item.cid10}</span>}
                              {item.dsm5 && <span>DSM: {item.dsm5}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="catalogo-mobile-actions mt-3 d-flex flex-wrap gap-2">
                        {item.ativo ? (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary flex-grow-1"
                              onClick={() => iniciarEdicao(item)}
                              disabled={salvando}
                            >
                              <i className="fas fa-edit"></i> Editar
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger flex-grow-1"
                              onClick={() => confirmarExclusao(item)}
                              disabled={salvando}
                            >
                              <i className="fas fa-trash"></i> Excluir
                            </button>
                          </>
                        ) : (
                          <button
                            className="btn btn-sm btn-success w-100"
                            onClick={() => reativarItem(item)}
                            disabled={salvando}
                          >
                            {salvando ? <span className="spinner-border spinner-border-sm"></span> : (<><i className="fas fa-undo"></i> Reativar</>)}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
              {/* Renderização DESKTOP - tabela */}
              <div className="table-responsive d-none d-md-block">
                <table className="table table-striped table-hover mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}>Ordem</th>
                      <th>Valor</th>
                      {abaSelecionada === 'problemasClinicos' && <th>Código</th>}
                      <th>Categoria</th>
                      {abaSelecionada === 'sintomas' && <th>Tipo Avaliação</th>}
                      {abaSelecionada === 'humor' && <th>Cor</th>}
                      {abaSelecionada === 'problemasClinicos' && <th>CID-10 / DSM-5</th>}
                      <th style={{ width: '100px' }}>Status</th>
                      <th style={{ width: '150px' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items
                      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
                      .map((item, index) => (
                        <tr key={item.id} className={!item.ativo ? 'table-secondary' : ''}>
                          <td><span className="badge badge-secondary">{item.ordem || index}</span></td>
                          <td>
                            <strong className={!item.ativo ? 'text-muted' : ''}>{item.valor}</strong>
                            {item.descricao && (<><br /><small className="text-muted">{item.descricao}</small></>)}
                          </td>
                          {abaSelecionada === 'problemasClinicos' && <td><code>{item.codigo || '-'}</code></td>}
                          <td>{item.categoria && (<span className={`badge badge-${config.color}`}>{item.categoria}</span>)}</td>
                          {abaSelecionada === 'sintomas' && <td><span className="badge badge-info">{item.tipo_avaliacao || 'ambos'}</span></td>}
                          {abaSelecionada === 'humor' && <td>{item.cor_indicativa && (
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-2" style={{ width: '20px', height: '20px', backgroundColor: item.cor_indicativa }}></div>
                              <small>{item.cor_indicativa}</small>
                            </div>
                          )}</td>}
                          {abaSelecionada === 'problemasClinicos' && <td><small>{item.cid10 && <div>CID: {item.cid10}</div>}{item.dsm5 && <div>DSM: {item.dsm5}</div>}</small></td>}
                          <td><span className={`badge badge-${item.ativo ? 'success' : 'secondary'}`}>{item.ativo ? 'Ativo' : 'Inativo'}</span></td>
                          <td>
                            {item.ativo ? (
                              <div className="btn-group">
                                <button className="btn btn-sm btn-outline-primary" onClick={() => iniciarEdicao(item)} disabled={salvando}><i className="fas fa-edit"></i></button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => confirmarExclusao(item)} disabled={salvando}><i className="fas fa-trash"></i></button>
                              </div>
                            ) : (
                              <button className="btn btn-sm btn-success" onClick={() => reativarItem(item)} disabled={salvando}>{salvando ? <span className="spinner-border spinner-border-sm"></span> : (<><i className="fas fa-undo"></i> Reativar</>)}</button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (carregando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="sr-only">Carregando...</span>
          </div>
          <p>Carregando catálogos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Alertas */}
      {erro && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setErro(null)}>
            <span>&times;</span>
          </button>
          <i className="icon fas fa-exclamation-triangle"></i>
          {erro}
        </div>
      )}

      {sucesso && (
        <div className="alert alert-success alert-dismissible">
          <button type="button" className="close" onClick={() => setSucesso(null)}>
            <span>&times;</span>
          </button>
          <i className="icon fas fa-check"></i>
          {sucesso}
        </div>
      )}

      {/* Navegação por abas */}
      <div className="card card-primary card-outline card-tabs">
        <div className="card-header p-0 pt-1 border-bottom-0">
          <ul className="nav nav-tabs" role="tablist">
            {Object.entries(configCatalogos).map(([key, config]) => (
              <li className="nav-item" key={key}>
                <a
                  className={`nav-link ${abaSelecionada === key ? 'active' : ''}`}
                  onClick={() => {
                    setAbaSelecionada(key as CatalogoTipo);
                    setModoEdicao(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAbaSelecionada(key as CatalogoTipo);
                      setModoEdicao(false);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                  role="tab"
                  tabIndex={0}
                >
                  <i className={config.icon}></i> {config.titulo}
                  <span className={`badge badge-${config.color} ml-2`}>
                    {catalogos[key as CatalogoTipo].length}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          {modoEdicao ? renderFormulario() : renderLista()}
        </div>
      </div>

      {/* Modal de confirmação de exclusão */}
      {mostrarModal && itemParaExcluir && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-warning"></i>
                  Confirmar Exclusão
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setMostrarModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Tem certeza que deseja excluir o item <strong>"{itemParaExcluir.valor}"</strong>?
                </p>
                {itemParaExcluir.descricao && (
                  <small className="text-muted">
                    Descrição: {itemParaExcluir.descricao}
                  </small>
                )}
                <div className="alert alert-warning mt-3">
                  <i className="fas fa-exclamation-triangle"></i>
                  Esta ação não pode ser desfeita!
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setMostrarModal(false)}
                  disabled={salvando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={excluirItem}
                  disabled={salvando}
                >
                  {salvando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Excluindo...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash"></i>
                      Confirmar Exclusão
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
